import { useState } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { X, FileText, Download, Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import { resolveBilledFrom, type SellerRegistration } from '../utils/invoiceAddress';
import PaymentLinkPanel from './commerce/PaymentLinkPanel';

// ─── Types ─────────────────────────────────────────────────────────────

export interface GymPurchase {
  id: string;
  invoiceId: string | null;
  status: string;
  /**
   * The sale's plan status, read from its plans' dates by the list endpoint:
   * active | paused | upcoming | expired | cancelled | refund | transferred |
   * pending | removed — null when the sale has no plan in it.
   */
  effectiveStatus?: string | null;
  paymentStatus: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  refundAmount: number;
  dueDate: string | null;
  overallDiscountAmount: number;
  overallDiscountType: string;
  overallDiscountValue: number;
  notes: string | null;
  createdAt: string;
  invoiceGstin?: string | null;
  invoiceGstinEnabled?: boolean | null;
  documentType?: string;
  recipientGstin?: string | null;
  recipientLegalName?: string | null;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  /** The registration the sale was made under: a Tax Invoice's legal name and registered address. */
  gstRegistration?: SellerRegistration | null;
  branch?: {
    id: string;
    name: string;
    displayName: string;
    addressLine?: string | null;
    city?: string | null;
    pincode?: string | null;
    gstin?: string | null;
    includeGstinInInvoices?: boolean | null;
  } | null;
  member: {
    id: string; 
    fullName: string | null; 
    displayName: string | null; 
    phoneNumber: string | null; 
    email: string | null; 
    address?: string | null; 
    city?: string | null; 
    pincode?: string | null 
  };
  gym?: { 
    id: string; 
    name: string; 
    displayName: string; 
    gstin?: string | null; 
    registeredAddress?: string | null; 
    address?: string | null;
    email?: string; 
    phoneNumbers?: string[];
    includeGstinInInvoices?: boolean;
  } | null;
  lineItems: Array<{
    id: string; 
    itemType: string; 
    name: string; 
    price: number; 
    quantity: number;
    gstPercent: number; 
    discount: number; 
    discountType: string;
    discountValue: number;
    metadata: Record<string, any>;
  }>;
  payments: Array<{
    id: string; 
    amount: number; 
    paymentMethod: string;
    paymentDate: string; 
    notes: string | null;
    taxAmount?: number;
  }>;
  creator?: { id: string; name: string | null; displayName: string | null } | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  purchase: GymPurchase | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────

function numberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';
  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 !== 0 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + convert(num % 10000000) : '');
  };
  return convert(Math.floor(num));
}

// Safe short-date formatter for invoice line items. Membership line-item metadata
// reliably carries a startDate but only sometimes an endDate, so `new Date(x)` on a
// missing/invalid value renders the literal string "Invalid Date". Returns null
// instead, letting callers omit the segment rather than print garbage.
function formatShortDate(value: unknown): string | null {
  if (!value) return null;
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ViewGymPurchaseModal({ isOpen, onClose, purchase }: Props) {
  const [downloading, setDownloading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  if (!isOpen || !purchase) return null;

  const handleSendInvoiceWhatsApp = async () => {
    if (!purchase) return;
    setSendingInvoice(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE_URL}/api/gym-purchases/${purchase.id}/send-invoice-whatsapp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        toast.error(json.error || 'Failed to send invoice');
        return;
      }
      toast.success('Invoice sent on WhatsApp');
    } catch {
      toast.error('Failed to send invoice');
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!purchase) return;
    setDownloading(true);
    try {
      const el = document.getElementById('purchase-invoice-content');
      if (!el) return toast.error('Invoice content not found');
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: 0.5,
        filename: `invoice-${purchase.invoiceId || purchase.id}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' as const },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(opt).from(el).save();
      toast.success('Invoice exported successfully');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  const paymentStatus = (() => {
    const ps = purchase.paymentStatus;
    if ((ps === 'due' || ps === 'partial') && purchase.dueDate && new Date(purchase.dueDate) < new Date()) return 'overdue';
    return ps;
  })();

  const isCancelled = purchase.status === 'cancelled';

  // Immutable sale-time snapshot. A bill of supply carries no GST and shows no
  // GSTIN; a tax invoice shows the GSTIN that was in effect at the time of sale.
  const isBillOfSupply = purchase.documentType === 'bill_of_supply';
  const displayGstin = purchase.invoiceGstin;
  const sellerName = purchase.gym?.displayName || purchase.gym?.name || 'Gym';
  const billedFrom = resolveBilledFrom(purchase, sellerName);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 lg:p-10 overflow-hidden" {...backdropClose(onClose)}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl h-full max-h-[95dvh] overflow-hidden flex flex-col scale-in-center" onClick={(e) => e.stopPropagation()}>
        
        {/* Modal Header Bar */}
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-800 px-6 sm:px-10 py-5 sm:py-6 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">{isBillOfSupply ? 'Bill of Supply' : 'Tax Invoice'}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${paymentStatus === 'paid' ? 'bg-emerald-500' : paymentStatus === 'overdue' ? 'bg-rose-500 animate-pulse' : 'bg-amber-500'}`}>
                  {paymentStatus}
                </span>
                <span className="text-xs text-blue-100/60 font-medium">#{purchase.invoiceId || purchase.id.slice(0, 8).toUpperCase()}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 group">
            <button
              onClick={handleSendInvoiceWhatsApp}
              disabled={sendingInvoice}
              className="px-3 sm:px-6 py-2.5 bg-white/10 text-white border border-white/20 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 shrink-0"
            >
              {sendingInvoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{sendingInvoice ? 'Sending...' : 'Send Invoice'}</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="px-3 sm:px-6 py-2.5 bg-white text-indigo-700 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-blue-50 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 shrink-0"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{downloading ? 'Exporting...' : 'Export PDF'}</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors shrink-0">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Collect online — outside the invoice content so it never lands in the PDF */}
        {purchase.gym?.id && !isCancelled && Number(purchase.pendingAmount) > 0 && (
          <PaymentLinkPanel
            gymId={purchase.gym.id}
            memberId={purchase.member.id}
            purchaseId={purchase.id}
            pendingAmount={Number(purchase.pendingAmount)}
            memberName={purchase.member.displayName || purchase.member.fullName}
            memberPhone={purchase.member.phoneNumber}
          />
        )}

        {/* Invoice Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-12 bg-slate-50 custom-scrollbar">
          <div id="purchase-invoice-content" className="bg-white p-8 sm:p-16 shadow-sm border border-slate-100 mx-auto w-full sm:max-w-[210mm] text-sm relative" style={{ fontFamily: '"Inter", sans-serif' }}>
            
            {/* Watermark */}
            {isCancelled && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden opacity-[0.05] z-50">
                <div className="text-[140px] font-black uppercase tracking-[0.2em] -rotate-45 border-[20px] px-20 py-8 rounded-[80px] whitespace-nowrap text-red-600 border-red-600">
                  CANCELLED
                </div>
              </div>
            )}

            {(paymentStatus === 'overdue' || paymentStatus === 'due' || paymentStatus === 'partial') && !isCancelled && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden opacity-[0.03]">
                <div className={`text-[120px] font-black uppercase tracking-[0.2em] -rotate-45 border-[16px] px-16 py-6 rounded-[60px] whitespace-nowrap ${paymentStatus === 'overdue' ? 'text-red-600 border-red-600' : 'text-amber-500 border-amber-500'}`}>
                  {paymentStatus === 'overdue' ? 'Overdue' : 'Payment Due'}
                </div>
              </div>
            )}

            {/* Header: TAX INVOICE */}
            <div className="flex flex-col sm:flex-row justify-between items-start mb-8 gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">{isBillOfSupply ? 'BILL OF SUPPLY' : 'TAX INVOICE'}</h1>
                <p className="text-gray-400 font-medium text-sm mt-1">
                  Invoice No: <span className="text-gray-800 font-bold">{purchase.invoiceId || purchase.id.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Invoice Date</p>
                <p className="text-lg font-bold text-gray-900 leading-none">
                  {new Date(purchase.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Parties Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 mb-10 border-t border-gray-100 pt-8">
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Billed From</p>
                  <p className="text-xl font-bold text-gray-900 leading-tight">{sellerName}</p>
                  <div className="text-sm text-gray-500 mt-2 space-y-1 font-medium leading-relaxed">
                    {billedFrom.legalName && <p className="text-gray-700">{billedFrom.legalName}</p>}
                    {billedFrom.address && <p>{billedFrom.address}</p>}
                    {billedFrom.branchAddress && <p>Branch: {billedFrom.branchAddress}</p>}
                    {!isBillOfSupply && displayGstin && <p className="font-bold text-gray-900 mt-1">GSTIN: {displayGstin}</p>}
                    <div className="pt-2 text-xs text-gray-500 space-y-0.5">
                      {purchase.gym?.email && <p><span className="font-bold text-gray-400 w-12 inline-block">Email:</span> {purchase.gym.email}</p>}
                      {purchase.gym?.phoneNumbers?.[0] && <p><span className="font-bold text-gray-400 w-12 inline-block">Phone:</span> {purchase.gym.phoneNumbers[0]}</p>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Billed To</p>
                  <p className="text-xl font-bold text-gray-900 leading-tight">{purchase.member.displayName || purchase.member.fullName || 'Member Name'}</p>
                  <div className="text-sm text-gray-500 mt-2 space-y-1 font-medium leading-relaxed">
                    {purchase.member.address && <p>{purchase.member.address}</p>}
                    {purchase.member.city && <p>{purchase.member.city}{purchase.member.pincode ? ` - ${purchase.member.pincode}` : ''}</p>}
                    {purchase.member.phoneNumber && <p className="mt-1 font-bold text-gray-800">Mob: {purchase.member.phoneNumber}</p>}
                    {/* Buyer GST details (B2B) — only on a Tax Invoice */}
                    {!isBillOfSupply && purchase.recipientGstin && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        {purchase.recipientLegalName && <p className="font-bold text-gray-800">{purchase.recipientLegalName}</p>}
                        <p className="text-gray-600">GSTIN: <span className="font-bold text-gray-900">{purchase.recipientGstin}</span></p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Payment Method</p>
                  <span className="inline-block bg-gray-100 text-gray-900 text-[10px] font-black px-3 py-1 rounded uppercase tracking-widest">
                    {purchase.payments?.[0]?.paymentMethod || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="mb-10">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-gray-900">
                    <th className="py-3 text-left text-[11px] font-black uppercase tracking-widest text-gray-900">Description</th>
                    <th className="py-3 px-3 text-center text-[11px] font-black uppercase tracking-widest text-gray-900 w-20">Qty</th>
                    <th className="py-3 px-3 text-right text-[11px] font-black uppercase tracking-widest text-gray-900 w-32">Unit Price</th>
                    <th className="py-3 text-right text-[11px] font-black uppercase tracking-widest text-gray-900 w-32">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {purchase.lineItems.map(item => (
                    <tr key={item.id}>
                      <td className="py-5 pr-4">
                        <p className="font-bold text-gray-900 text-base">{item.name}</p>
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">{item.itemType}</p>
                        {item.itemType === 'membership' && formatShortDate(item.metadata?.startDate) && (
                          <p className="text-[10px] text-gray-400 mt-1 italic font-medium">
                            {formatShortDate(item.metadata.startDate)}
                            {formatShortDate(item.metadata.endDate) && ` - ${formatShortDate(item.metadata.endDate)}`}
                          </p>
                        )}
                        {Number(item.discountValue) > 0 && (
                          <p className="text-[10px] text-emerald-600 font-bold mt-1 uppercase tracking-tight">
                            Discount: {item.discountType === 'percent' ? `${item.discountValue}%` : `₹${Number(item.discountValue).toLocaleString()}`}
                          </p>
                        )}
                      </td>
                      <td className="py-5 px-3 text-center font-bold text-gray-900">{Number(item.quantity).toFixed(2)}</td>
                      <td className="py-5 px-3 text-right font-medium text-gray-900">₹{Number(item.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="py-5 text-right font-bold text-gray-900">
                        <div className="flex flex-col items-end">
                           <span>₹{(Number(item.price) * Number(item.quantity)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                           {Number(item.discount) > 0 && (
                             <span className="text-[10px] text-emerald-600 font-medium tracking-tighter cursor-default" title="Calculated line discount">
                               −₹{(Number(item.discount) * Number(item.quantity)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                             </span>
                           )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-8 border-t border-gray-100">
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Amount in Words</p>
                  <p className="text-sm font-bold text-gray-600 capitalize leading-relaxed">
                    {numberToWords(Math.round(purchase.totalAmount))} Rupees Only
                  </p>
                </div>
                {purchase.notes && (
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Remarks</p>
                    <p className="text-xs text-gray-500 italic leading-relaxed">"{purchase.notes}"</p>
                  </div>
                )}
                <div className="pt-4 border-t border-gray-50">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Terms & Conditions</p>
                  <ul className="text-[9px] text-gray-400 list-disc list-inside space-y-1 font-medium">
                    <li>Payments are non-refundable & non-transferable.</li>
                    <li>Receipt is invalid without professional gym verification.</li>
                    <li>Refer to membership contract for detailed terms.</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Base Amount</span>
                  <span className="text-gray-900 font-bold">₹{Number(purchase.subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                {Number(purchase.discountAmount) - Number(purchase.overallDiscountAmount) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-emerald-500 font-bold uppercase text-[10px] tracking-widest">Item Discounts</span>
                    <span className="text-emerald-600 font-bold">-₹{(Number(purchase.discountAmount) - Number(purchase.overallDiscountAmount)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {!isBillOfSupply && Number(purchase.taxAmount) > 0 && (
                  Number(purchase.igstAmount) > 0 ? (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">IGST</span>
                      <span className="text-gray-900 font-bold">₹{Number(purchase.igstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ) : (Number(purchase.cgstAmount) > 0 || Number(purchase.sgstAmount) > 0) ? (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">CGST</span>
                        <span className="text-gray-900 font-bold">₹{Number(purchase.cgstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">SGST</span>
                        <span className="text-gray-900 font-bold">₹{Number(purchase.sgstAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">GST</span>
                      <span className="text-gray-900 font-bold">₹{Number(purchase.taxAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )
                )}
                {Number(purchase.overallDiscountAmount) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-indigo-500 font-bold uppercase text-[10px] tracking-widest">Overall Discount</span>
                    <span className="text-indigo-600 font-bold">-₹{Number(purchase.overallDiscountAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="pt-5 mt-2 border-t-2 border-gray-900 flex justify-between items-center">
                  <span className="text-xl font-black text-gray-900 uppercase tracking-tight">Grand Total</span>
                  <span className="text-2xl font-black text-blue-600 tracking-tighter leading-none">
                    ₹{Number(purchase.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {Number(purchase.refundAmount) > 0 && (
                  <div className="pt-3 flex justify-between items-center">
                    <span className="text-red-500 font-bold uppercase text-[10px] tracking-widest">Amount Refunded</span>
                    <span className="text-xl font-black text-red-600 tracking-tighter leading-none">₹{Number(purchase.refundAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <p className="text-right mt-1 text-[9px] text-gray-300 font-bold uppercase tracking-widest">Inclusive of all taxes</p>
              </div>
            </div>

            {/* Payment Schedule Section (Summary + History) */}
            {(paymentStatus === 'partial' || paymentStatus === 'due' || paymentStatus === 'overdue' || purchase.payments?.length > 0) && (
              <div className="mt-12 pt-10 border-t border-gray-100" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Payment Schedule & History</p>
                
                {/* Summary Grid for Partial/Due/Overdue */}
                {(paymentStatus === 'partial' || paymentStatus === 'due' || paymentStatus === 'overdue') && !isCancelled && (
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-slate-50 p-4 rounded-2xl text-center shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Order Value</p>
                      <p className="text-base font-black text-slate-900">₹{Number(purchase.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-2xl text-center shadow-sm">
                      <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Received Amount</p>
                      <p className="text-base font-black text-emerald-700">₹{Number(purchase.paidAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className={`${paymentStatus === 'overdue' ? 'bg-rose-50' : 'bg-amber-50'} p-4 rounded-2xl text-center shadow-sm border ${paymentStatus === 'overdue' ? 'border-rose-100' : 'border-amber-100'}`}>
                      <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${paymentStatus === 'overdue' ? 'text-rose-600' : 'text-amber-600'}`}>
                        {paymentStatus === 'overdue' ? 'Overdue Debt' : 'Scheduled Balance'}
                      </p>
                      <p className={`text-base font-black ${paymentStatus === 'overdue' ? 'text-rose-700' : 'text-amber-700'}`}>₹{Number(purchase.pendingAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                )}

                {/* Date Reference for Pending */}
                {purchase.pendingAmount > 0 && purchase.dueDate && !isCancelled && (
                  <div className="mb-8 flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
                    <div className={`w-2 h-2 rounded-full ${paymentStatus === 'overdue' ? 'bg-rose-500 animate-pulse' : 'bg-amber-500'}`}></div>
                    <p className={`text-[11px] font-black uppercase tracking-widest ${paymentStatus === 'overdue' ? 'text-rose-600' : 'text-amber-600'}`}>
                      {paymentStatus === 'overdue' ? '⚠ breached deadline: ' : 'Scheduled Payment by: '}
                      {new Date(purchase.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                )}

                {/* History Table */}
                {purchase.payments?.length > 0 && (
                  <div className="space-y-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 opacity-70">Transaction Records</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b-2 border-gray-900">
                          <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left w-10">S.No</th>
                          <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-right">Amount</th>
                          <th className="py-2 px-4 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Payment Date</th>
                          <th className="py-2 px-4 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Method</th>
                          <th className="py-2 text-[10px] font-black uppercase tracking-widest text-gray-900 text-left">Note/Reference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {purchase.payments.map((p, idx) => (
                          <tr key={p.id}>
                            <td className="py-3 text-[10px] font-black text-gray-300">{(idx + 1).toString().padStart(2, '0')}</td>
                            <td className="py-3 text-right font-black text-gray-900">₹{Number(p.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td className="py-3 px-4 font-bold text-gray-600 uppercase text-[10px] tracking-wider">
                              {new Date(p.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="py-3 px-4 font-black text-gray-400 uppercase text-[9px] tracking-widest">
                              <span className="bg-slate-100 px-2 py-0.5 rounded">{p.paymentMethod}</span>
                            </td>
                            <td className="py-3 text-gray-400 italic text-[10px] max-w-[150px] truncate">{p.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-[3px] border-gray-900 font-black">
                          <td className="pt-3 text-[10px] uppercase text-gray-400">Total Received</td>
                          <td className="pt-3 text-right text-gray-900">
                             ₹{purchase.payments.reduce((sum, p) => sum + Number(p.amount), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="mt-16 pt-10 border-t border-gray-50 text-center">
              <p className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em]">Computer Generated Receipt • Dynamic Administrative Document</p>
              <p className="text-[8px] text-gray-300 font-bold uppercase tracking-widest mt-2">Generated {new Date().toLocaleString('en-IN')}</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
